import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { CalendarDays, Plus, Check, X, Ban } from "lucide-react";
import { toast } from "sonner";
import { todayIST } from "../lib/datefmt";

const TEAM_LEAD_ROLES = ["admin", "director", "rm_and_above", "manager", "sales_manager"];

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-700",
};

function daysBetween(from, to) {
  if (!from || !to) return 0;
  const a = new Date(from);
  const b = new Date(to);
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

export default function LeavesPage() {
  const { user } = useAuth();
  const canApprove = TEAM_LEAD_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const initial = {
    from_date: todayIST(),
    to_date: todayIST(),
    leave_type: "casual",
    reason: "",
  };
  const [form, setForm] = useState(initial);

  const load = async () => {
    try {
      const r = await api.get("/leaves");
      setRows(r.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) return toast.error("Reason is required");
    if (form.to_date < form.from_date) return toast.error("End date cannot be before start date");
    try {
      await api.post("/leaves", form);
      toast.success("Leave requested — your manager has been notified");
      setForm(initial);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const act = async (row, action) => {
    let remarks = "";
    if (action !== "cancel") {
      remarks = window.prompt(`${action === "approve" ? "Approval" : "Rejection"} remarks (optional):`) || "";
    } else if (!window.confirm("Cancel this leave request?")) {
      return;
    }
    try {
      await api.post(`/leaves/${row.id}/action`, { action, remarks });
      toast.success(`Leave ${action}d`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const { pending, approved } = useMemo(() => {
    let p = 0, a = 0;
    for (const r of rows) {
      if (r.status === "pending") p += 1;
      else if (r.status === "approved") a += daysBetween(r.from_date, r.to_date);
    }
    return { pending: p, approved: a };
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Leave Requests"
        subtitle="Submit and approve time-off requests up the chain of command"
        actions={
          <Button onClick={() => setOpen((s) => !s)} data-testid="new-leave-btn">
            <Plus className="w-4 h-4" /> Request leave
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Pending Requests</div>
          <div className="text-3xl font-heading font-black text-amber-700" data-testid="leaves-pending">{pending}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Approved Days (all-time)</div>
          <div className="text-3xl font-heading font-black text-emerald-700" data-testid="leaves-approved">{approved}</div>
        </Card>
      </div>

      {open && (
        <Card className="mb-6">
          <h3 className="font-heading text-xl font-bold mb-4">Request leave</h3>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>From date</Label>
              <Input
                type="date" required
                value={form.from_date}
                onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                data-testid="leave-from"
              />
            </div>
            <div>
              <Label>To date</Label>
              <Input
                type="date" required
                value={form.to_date}
                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                data-testid="leave-to"
              />
            </div>
            <div>
              <Label>Leave type</Label>
              <Select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="leave-type">
                <option value="casual">Casual</option>
                <option value="sick">Sick</option>
                <option value="earned">Earned</option>
                <option value="unpaid">Unpaid</option>
              </Select>
            </div>
            <div>
              <Label>Total days</Label>
              <Input value={daysBetween(form.from_date, form.to_date)} disabled />
            </div>
            <div className="sm:col-span-2">
              <Label>Reason</Label>
              <Input
                required
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. family function, medical, personal work"
                data-testid="leave-reason"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="leave-submit">Submit request</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState icon={CalendarDays} title="No leave requests yet" description="Submit your first request above." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Requester</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Days</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actioned by</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isMine = r.user_id === user?.id;
                  return (
                    <tr key={r.id} className="border-b border-[#E5E2DC] align-top" data-testid={`leave-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.user_name}</div>
                        <div className="text-xs text-[#5C6B5E]">{r.user_role}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.from_date}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.to_date}</td>
                      <td className="px-4 py-3">{daysBetween(r.from_date, r.to_date)}</td>
                      <td className="px-4 py-3 capitalize">{r.leave_type}</td>
                      <td className="px-4 py-3 text-[#5C6B5E] max-w-xs">{r.reason}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${STATUS_STYLE[r.status] || ""}`}
                          data-testid={`leave-status-${r.id}`}
                        >
                          {r.status}
                        </span>
                        {r.action_remarks && (
                          <div className="text-[11px] text-[#5C6B5E] mt-1 italic">"{r.action_remarks}"</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5C6B5E]">
                        {r.action_by_name || "—"}
                        {r.action_at && <div>{r.action_at.slice(0, 10)}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {r.status === "pending" && canApprove && !isMine && (
                            <>
                              <button
                                onClick={() => act(r, "approve")}
                                className="inline-flex items-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700 text-[11px] font-semibold px-2 py-1 rounded"
                                data-testid={`leave-approve-${r.id}`}
                              >
                                <Check className="w-3 h-3" /> Approve
                              </button>
                              <button
                                onClick={() => act(r, "reject")}
                                className="inline-flex items-center gap-1 bg-red-600 text-white hover:bg-red-700 text-[11px] font-semibold px-2 py-1 rounded"
                                data-testid={`leave-reject-${r.id}`}
                              >
                                <X className="w-3 h-3" /> Reject
                              </button>
                            </>
                          )}
                          {r.status === "pending" && isMine && (
                            <button
                              onClick={() => act(r, "cancel")}
                              className="inline-flex items-center gap-1 bg-gray-500 text-white hover:bg-gray-600 text-[11px] font-semibold px-2 py-1 rounded"
                              data-testid={`leave-cancel-${r.id}`}
                            >
                              <Ban className="w-3 h-3" /> Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
